import React, { useEffect, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import { CircularProgress } from '@mui/material'
import { t } from 'i18next'
import { CustomSwitch } from 'Components/Switch/CustomSwitch'
import CustomSnackbar from 'Components/CustomSnackbar'
import {
  getPaymentMethodConfig,
  savePaymentMethodConfig,
  updatePaymentMethodStatus,
} from 'Api/paymentConfig/paymentConfig.services'
import { encryptField } from 'Utils/paymentConfigEncryption'
import {
  createAbortableRequest,
  isAbortError,
  PAYMENT_CONFIG_REQUEST_TIMEOUT_MS,
} from '../../utils/request.utils'

const hasStoredFieldValue = value =>
  value !== null && value !== undefined && value !== ''

function buildFieldValues(configFields, fields) {
  const fieldsByKey = {}
  fields.forEach(f => {
    fieldsByKey[f.FieldKey] = f
  })
  const values = {}
  const configuredKeys = {}
  const sensitiveKeysInConfig = {}
  configFields.forEach(f => {
    const field = fieldsByKey[f.FieldKey]

    if (!field) return

    if (f.IsSensitive || field.IsSensitive) {
      sensitiveKeysInConfig[f.FieldKey] = true
      const hasStoredSensitiveValue =
        f.HasValue === true ||
        f.IsConfigured === true ||
        hasStoredFieldValue(f.FieldValue)

      if (hasStoredSensitiveValue) {
        configuredKeys[f.FieldKey] = true
      }
    } else if (hasStoredFieldValue(f.FieldValue)) {
      values[field.FieldID] = f.FieldValue
    }
  })
  return { values, configuredKeys, sensitiveKeysInConfig }
}

function buildConfiguredSensitiveKeys(fields) {
  return fields.reduce((keys, field) => {
    if (field.IsSensitive) {
      keys[field.FieldKey] = true
    }

    return keys
  }, {})
}

export default function YappyCompanyCard({
  company,
  publicKey,
  publicKeyError,
}) {
  const {
    label,
    value,
    companyId,
    paymentTypeId,
    fields,
    isLoading: parentLoading,
    error: parentError,
  } = company

  const fieldRefs = useRef({})

  const [fieldValues, setFieldValues] = useState({})
  const [initialFieldValues, setInitialFieldValues] = useState({})
  const [sensitiveHasValue, setSensitiveHasValue] = useState({})
  const [configuredKeys, setConfiguredKeys] = useState({})
  const [editingFields, setEditingFields] = useState({})
  const [isActive, setIsActive] = useState(false)
  const [initialIsActive, setInitialIsActive] = useState(false)
  const [companyHasConfig, setCompanyHasConfig] = useState(false)
  const [configAttempted, setConfigAttempted] = useState(false)
  const [configError, setConfigError] = useState(null)
  const [isLoadingConfig, setIsLoadingConfig] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success',
  })

  useEffect(() => {
    if (!paymentTypeId || !companyId) return

    const { requestConfig, isMounted, abort } = createAbortableRequest()

    setIsLoadingConfig(true)
    setConfigError(null)
    getPaymentMethodConfig(paymentTypeId, companyId, requestConfig)
      .then(({ data }) => {
        if (!isMounted()) return

        const config = data.Data
        const enabled = config.IsEnabled ?? false
        setIsActive(enabled)
        setInitialIsActive(enabled)
        const {
          values,
          configuredKeys: configured,
          sensitiveKeysInConfig,
        } = buildFieldValues(config.Fields || [], fields)
        const hasStoredSensitiveConfig = Object.keys(configured).length > 0
        const hasStoredNonSensitiveConfig = Object.keys(values).length > 0
        const backendReturnedSensitiveMetadata =
          Object.keys(sensitiveKeysInConfig).length > 0
        const companyHasStoredConfig =
          enabled || hasStoredNonSensitiveConfig || hasStoredSensitiveConfig
        const shouldInferStoredSensitiveKeys =
          !hasStoredSensitiveConfig &&
          (enabled ||
            (backendReturnedSensitiveMetadata && hasStoredNonSensitiveConfig))
        const sensitiveKeys = shouldInferStoredSensitiveKeys
          ? buildConfiguredSensitiveKeys(fields)
          : {}

        setFieldValues(values)
        setInitialFieldValues(values)
        setConfiguredKeys({ ...sensitiveKeys, ...configured })
        setCompanyHasConfig(companyHasStoredConfig)
      })
      .catch(error => {
        if (isAbortError(error) || !isMounted()) return

        const configWasNotFound = error?.response?.status === 404

        setFieldValues({})
        setInitialFieldValues({})
        setConfiguredKeys({})
        setSensitiveHasValue({})
        setEditingFields({})
        setIsActive(false)
        setInitialIsActive(false)
        setCompanyHasConfig(false)

        if (configWasNotFound) {
          setConfigError(null)
          return
        }

        console.error('No fue posible cargar la configuración de Yappy:', error)
        setConfigError(
          t('SETTINGS_T.ERROR_LOADING', {
            defaultValue: 'No fue posible cargar la configuración de Yappy.',
          })
        )
      })
      .finally(() => {
        if (!isMounted()) return

        setIsLoadingConfig(false)
        setConfigAttempted(true)
      })

    return abort
  }, [paymentTypeId, companyId, fields])

  const isBusy = isSaving || isLoadingConfig || parentLoading

  const hasCredentials =
    !parentLoading &&
    !isLoadingConfig &&
    fields.length > 0 &&
    fields.every(field => {
      const required = field.IsRequired ?? true
      if (!required) return true
      if (field.IsSensitive) {
        const buttonIsShown =
          configuredKeys[field.FieldKey] &&
          companyHasConfig &&
          !editingFields[field.FieldID]
        return buttonIsShown || sensitiveHasValue[field.FieldID] === true
      }
      return (fieldValues[field.FieldID] || '').trim() !== ''
    })

  const isStatusDirty = isActive !== initialIsActive

  const credentialsDirty = fields.some(field => {
    if (!field.IsSensitive) {
      return (
        (fieldValues[field.FieldID] || '') !==
        (initialFieldValues[field.FieldID] || '')
      )
    }
    return editingFields[field.FieldID] && sensitiveHasValue[field.FieldID]
  })

  const isDirty = isStatusDirty || credentialsDirty

  const closeSnackbar = () => setSnackbar(prev => ({ ...prev, open: false }))
  const showAlert = (message, severity) =>
    setSnackbar({ open: true, message, severity })

  const handleRegisterNewKey = fieldId => {
    setEditingFields(prev => ({ ...prev, [fieldId]: true }))
  }

  const handleFieldChange = (fieldId, val) => {
    setFieldValues(prev => ({ ...prev, [fieldId]: val }))
  }

  const handleSensitiveChange = (fieldId, hasValue) => {
    setSensitiveHasValue(prev => ({ ...prev, [fieldId]: hasValue }))
  }

  const handleSave = async e => {
    e.preventDefault()
    setIsSaving(true)
    const emptyEditing = Object.keys(editingFields).filter(
      id => !sensitiveHasValue[id]
    )
    if (emptyEditing.length) {
      setEditingFields(prev => {
        const next = { ...prev }
        emptyEditing.forEach(id => delete next[id])
        return next
      })
    }
    const reqConfig = { timeout: PAYMENT_CONFIG_REQUEST_TIMEOUT_MS }
    let credentialsSaved = false
    try {
      if (credentialsDirty) {
        const values = await Promise.all(
          fields
            .filter(
              field => !(field.IsSensitive && !sensitiveHasValue[field.FieldID])
            )
            .map(async field => {
              if (field.IsSensitive) {
                const raw = fieldRefs.current[field.FieldID]?.value || ''
                const ciphertext = await encryptField(raw, publicKey)
                return {
                  FieldID: field.FieldID,
                  FieldValue: ciphertext,
                  IsEncrypted: true,
                }
              }
              return {
                FieldID: field.FieldID,
                FieldValue: fieldValues[field.FieldID] || '',
                IsEncrypted: false,
              }
            })
        )
        await savePaymentMethodConfig(
          paymentTypeId,
          { CompanyId: companyId, Values: values },
          reqConfig
        )
        const savedSensitiveKeys = fields.reduce((keys, field) => {
          if (
            field.IsSensitive &&
            (configuredKeys[field.FieldKey] || sensitiveHasValue[field.FieldID])
          ) {
            keys[field.FieldKey] = true
          }
          return keys
        }, {})
        fields.forEach(field => {
          if (field.IsSensitive && fieldRefs.current[field.FieldID]) {
            fieldRefs.current[field.FieldID].value = ''
          }
        })
        setSensitiveHasValue({})
        setEditingFields({})
        setConfiguredKeys(prev => ({ ...prev, ...savedSensitiveKeys }))
        setInitialFieldValues({ ...fieldValues })
        setCompanyHasConfig(true)
        credentialsSaved = true
      }

      if (isStatusDirty) {
        await updatePaymentMethodStatus(
          paymentTypeId,
          { CompanyId: companyId, IsEnabled: isActive },
          reqConfig
        )
        setInitialIsActive(isActive)
      }

      showAlert(
        t('SETTINGS_T.ALERT_SAVE_SUCCESS', { company: label }),
        'success'
      )
    } catch (error) {
      const key = credentialsSaved
        ? 'SETTINGS_T.ALERT_STATUS_ERROR'
        : !credentialsDirty
        ? 'SETTINGS_T.ALERT_STATUS_ONLY_ERROR'
        : 'SETTINGS_T.ALERT_SAVE_ERROR'
      showAlert(
        t(key, {
          company: label,
          error: error?.message || 'Error desconocido',
        }),
        'error'
      )
    } finally {
      setIsSaving(false)
    }
  }

  const cardHeader = (
    <div className="yappy-config-card-header">
      <div className="yappy-config-logo-wrapper">
        <div className="yappy-config-logo-icon">Y</div>
        <div className="yappy-config-card-title-group">
          <p className="yappy-config-company-name">{label}</p>
          <p className="yappy-config-connector-sub">
            {t('SETTINGS_T.YAPPY_SUB')}
          </p>
        </div>
      </div>
    </div>
  )

  if (parentError) {
    return (
      <div
        className="yappy-config-card yappy-config-card--error"
        data-testid={`form-yappy-config-${value}`}>
        {cardHeader}
        <p className="yappy-config-error-text">{parentError}</p>
      </div>
    )
  }

  if (parentLoading || !configAttempted) {
    return (
      <div
        className="yappy-config-card yappy-config-card--loading"
        data-testid={`form-yappy-config-${value}`}>
        <CircularProgress size={28} sx={{ color: '#022767' }} />
      </div>
    )
  }

  if (publicKeyError) {
    return (
      <div
        className="yappy-config-card yappy-config-card--error"
        data-testid={`form-yappy-config-${value}`}>
        {cardHeader}
        <p className="yappy-config-error-text">{publicKeyError}</p>
      </div>
    )
  }

  if (configError) {
    return (
      <div
        className="yappy-config-card yappy-config-card--error"
        data-testid={`form-yappy-config-${value}`}>
        {cardHeader}
        <p className="yappy-config-error-text">{configError}</p>
      </div>
    )
  }

  if (!publicKey) {
    return (
      <div
        className="yappy-config-card yappy-config-card--loading"
        data-testid={`form-yappy-config-${value}`}>
        <CircularProgress size={28} sx={{ color: '#022767' }} />
      </div>
    )
  }

  return (
    <>
      <form
        className="yappy-config-card"
        onSubmit={handleSave}
        data-testid={`form-yappy-config-${value}`}>
        <div className="yappy-config-card-header">
          <div className="yappy-config-logo-wrapper">
            <div className="yappy-config-logo-icon">Y</div>
            <div className="yappy-config-card-title-group">
              <p className="yappy-config-company-name">{label}</p>
              <p className="yappy-config-connector-sub">
                {t('SETTINGS_T.YAPPY_SUB')}
              </p>
            </div>
          </div>
          <div className="yappy-config-toggle-wrapper">
            <span
              className={`yappy-config-status-label ${
                isActive ? 'active' : 'inactive'
              }`}>
              {isActive ? t('SETTINGS_T.ACTIVE') : t('SETTINGS_T.INACTIVE')}
            </span>
            <CustomSwitch
              checked={isActive}
              onChange={e => setIsActive(e.target.checked)}
              disabled={isBusy}
              inputProps={{ 'aria-label': `toggle yappy activo ${label}` }}
              data-testid={`input-switch-toggle-${value}`}
            />
          </div>
        </div>

        <div className="yappy-config-fields">
          {fields.map(field =>
            field.IsSensitive ? (
              <div key={field.FieldID} className="yappy-config-field">
                <label
                  className="yappy-config-label"
                  htmlFor={`yappy-${field.FieldKey}-${value}`}>
                  {field.FieldLabel}
                  {field.IsRequired === false ? '' : ' *'}
                </label>
                {configuredKeys[field.FieldKey] &&
                companyHasConfig &&
                !editingFields[field.FieldID] ? (
                  <button
                    type="button"
                    className="yappy-config-btn-register-key"
                    onClick={() => handleRegisterNewKey(field.FieldID)}
                    disabled={isBusy}
                    data-testid={`btn-register-key-${field.FieldKey}-${value}`}>
                    {t('SETTINGS_T.BTN_REGISTER_NEW_KEY')}
                  </button>
                ) : (
                  <input
                    ref={el => {
                      fieldRefs.current[field.FieldID] = el
                    }}
                    id={`yappy-${field.FieldKey}-${value}`}
                    type="password"
                    className="yappy-config-input yappy-config-input--no-reveal"
                    placeholder={t('SETTINGS_T.SECRET_KEY_PLACEHOLDER_EMPTY')}
                    onChange={e =>
                      handleSensitiveChange(
                        field.FieldID,
                        e.target.value.length > 0
                      )
                    }
                    disabled={isBusy}
                    data-testid={`input-yappy-${field.FieldKey}-${value}`}
                    autoComplete="new-password"
                    required={field.IsRequired !== false}
                    autoFocus={!!editingFields[field.FieldID]}
                  />
                )}
              </div>
            ) : (
              <div key={field.FieldID} className="yappy-config-field">
                <label
                  className="yappy-config-label"
                  htmlFor={`yappy-${field.FieldKey}-${value}`}>
                  {field.FieldLabel}
                  {field.IsRequired === false ? '' : ' *'}
                </label>
                <input
                  id={`yappy-${field.FieldKey}-${value}`}
                  type="text"
                  className="yappy-config-input"
                  placeholder={t('SETTINGS_T.FIELD_PLACEHOLDER')}
                  value={fieldValues[field.FieldID] || ''}
                  onChange={e =>
                    handleFieldChange(field.FieldID, e.target.value)
                  }
                  disabled={isBusy}
                  data-testid={`input-yappy-${field.FieldKey}-${value}`}
                  autoComplete="off"
                  required={field.IsRequired !== false}
                />
              </div>
            )
          )}
        </div>

        <div className="yappy-config-actions">
          <button
            type="submit"
            className="yappy-config-btn-primary"
            disabled={!hasCredentials || !isDirty || isBusy}
            data-testid={`btn-yappy-save-${value}`}>
            {isSaving ? (
              <>
                <CircularProgress
                  size={14}
                  thickness={5}
                  sx={{ color: '#fff', mr: 1 }}
                />
                {t('SETTINGS_T.BTN_SAVING')}
              </>
            ) : (
              t('SETTINGS_T.BTN_SAVE')
            )}
          </button>
        </div>
      </form>

      <CustomSnackbar
        open={snackbar.open}
        message={snackbar.message}
        severity={snackbar.severity}
        onClose={closeSnackbar}
      />
    </>
  )
}

YappyCompanyCard.propTypes = {
  publicKey: PropTypes.object,
  publicKeyError: PropTypes.string,
  company: PropTypes.shape({
    label: PropTypes.string.isRequired,
    value: PropTypes.string.isRequired,
    companyId: PropTypes.string.isRequired,
    paymentTypeId: PropTypes.string,
    fields: PropTypes.arrayOf(
      PropTypes.shape({
        FieldID: PropTypes.string.isRequired,
        FieldKey: PropTypes.string.isRequired,
        FieldLabel: PropTypes.string.isRequired,
        IsSensitive: PropTypes.bool.isRequired,
        IsRequired: PropTypes.bool,
      })
    ).isRequired,
    isLoading: PropTypes.bool.isRequired,
    error: PropTypes.string,
  }).isRequired,
}
